// Audio reader — Amazon Polly server voice, free for every user (not just
// subscribers) up to Polly's monthly allowance. Falls back silently to the
// on-device Web Speech API voice if Polly ever fails or that allowance runs
// out, so listening never breaks — it just quietly gets a little more robotic.
// One narration plays at a time app-wide.
import { useEffect, useState } from "react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SILENT =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";

// Server TTS is always available, so the Listen buttons always render.
export const speechSupported = true;
const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
const browserTTS = !!synth && typeof window !== "undefined" && "SpeechSynthesisUtterance" in window;

// status: "idle" | "loading" | "playing" | "paused"
let state = { activeId: null, status: "idle", progress: 0 };
const listeners = new Set();
function emit() {
  const snap = { ...state };
  listeners.forEach((l) => l(snap));
}
export function subscribe(fn) {
  listeners.add(fn);
  fn({ ...state });
  return () => listeners.delete(fn);
}

let currentVoice = "joanna-neural";
let playbackRate = 1.0;
try {
  if (typeof localStorage !== "undefined") {
    currentVoice = localStorage.getItem("owai_tts_voice") || currentVoice;
    playbackRate = parseFloat(localStorage.getItem("owai_tts_rate")) || 1.0;
  }
} catch (e) { /* ignore */ }

let lastId = null;
let lastText = null;

export function getVoice() { return currentVoice; }
export function getRate() { return playbackRate; }
export function setVoice(v) {
  currentVoice = v || "joanna-neural";
  try { localStorage.setItem("owai_tts_voice", currentVoice); } catch (e) { /* ignore */ }
  // Voice change needs a fresh synth — restart the current narration if any.
  if (lastId && lastText && state.status !== "idle") play(lastId, lastText);
}
export function setRate(r) {
  playbackRate = Math.min(1.5, Math.max(0.5, r || 1.0));
  try { localStorage.setItem("owai_tts_rate", String(playbackRate)); } catch (e) { /* ignore */ }
  if (audioEl) audioEl.playbackRate = playbackRate; // instant, no re-synth
}

// --- Server voices (Amazon Polly) — the primary listening experience -------
// Kept in sync with the 6 voices in backend/polly_tts.py.
export const SERVER_VOICES = [
  { key: "danielle-neural", label: "Danielle" },
  { key: "gregory-neural", label: "Gregory" },
  { key: "matthew-neural", label: "Matthew" },
  { key: "stephen-neural", label: "Stephen" },
  { key: "joanna-neural", label: "Joanna" },
  { key: "joanna-standard", label: "Joanna (Classic)" },
];

// --- On-device fallback voice (Web Speech API) ------------------------------
// Only used silently when Polly fails or its free monthly allowance runs out.
const FALLBACK_VOICE_NAME = "Google US English";
// Novelty/robotic voices to hide from auto-selection.
const BLOCKED_VOICES = new Set(["fred", "albert", "bad news", "baah", "bahh", "bells"]);
const isBlocked = (v) => BLOCKED_VOICES.has((v.name || "").trim().toLowerCase());
function scoreVoice(v) {
  const n = (v.name || "").toLowerCase();
  let s = 0;
  if (/natural|neural/.test(n)) s += 60;
  if (/premium|enhanced/.test(n)) s += 50;
  if (/siri/.test(n)) s += 45;
  if (/google/.test(n)) s += 30;
  if (/online/.test(n)) s += 25;
  if (v.localService === false) s += 10;
  if (/en[-_]?us/i.test(v.lang || "")) s += 8;
  else if (/en[-_]?gb/i.test(v.lang || "")) s += 6;
  return s;
}
function englishVoices() {
  if (!synth) return [];
  const all = (synth.getVoices() || []).filter((v) => !isBlocked(v));
  const en = all.filter((v) => /^en(\b|[-_])/i.test(v.lang || ""));
  return en.length ? en : all;
}
export function splitSentences(text) {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (!clean) return [];
  return (clean.match(/[^.!?]+[.!?]*(\s|$)/g) || [clean]).map((s) => s.trim()).filter(Boolean);
}

let generation = 0;
let mode = null; // "server" | "browser"
let audioEl = null;
let currentUrl = null;
let realPlaying = false; // true only while the actual MP3 (not the silent prime) is loaded

function ensureAudio() {
  if (audioEl) return audioEl;
  audioEl = new Audio();
  audioEl.preload = "auto";
  audioEl.style.display = "none";
  try { if (typeof document !== "undefined") document.body.appendChild(audioEl); } catch (e) { /* ignore */ }
  audioEl.addEventListener("timeupdate", () => {
    if (mode === "server" && realPlaying && audioEl.duration) {
      state = { ...state, progress: Math.min(0.999, audioEl.currentTime / audioEl.duration) };
      emit();
    }
  });
  audioEl.addEventListener("ended", () => {
    // Only finish on the real MP3 that has actually reached its end — never on the
    // empty silent-prime (duration ~0) or a stale queued event after a src swap.
    if (mode !== "server" || !realPlaying) return;
    if (!audioEl.duration || audioEl.duration < 0.3) return;
    if (audioEl.currentTime < audioEl.duration - 0.6) return;
    finish();
  });
  return audioEl;
}

function revokeUrl() {
  if (currentUrl) {
    URL.revokeObjectURL(currentUrl);
    currentUrl = null;
  }
}

function finish() {
  state = { ...state, status: "idle", progress: 1 };
  emit();
  const g = generation;
  setTimeout(() => {
    if (g !== generation) return;
    state = { activeId: null, status: "idle", progress: 0 };
    emit();
  }, 500);
}

export function stop() {
  generation += 1;
  browserStop();
  realPlaying = false;
  if (audioEl) {
    try { audioEl.pause(); } catch (e) { /* ignore */ }
    try { audioEl.removeAttribute("src"); audioEl.load(); } catch (e) { /* ignore */ }
  }
  revokeUrl();
  mode = null;
  state = { activeId: null, status: "idle", progress: 0 };
  emit();
}

export async function play(id, text) {
  const t = (typeof text === "function" ? text() : text || "").trim();
  if (!t) return;
  stop();
  lastId = id;
  lastText = t;
  const gen = generation;
  state = { activeId: id, status: "loading", progress: 0 };
  emit();
  try {
    await serverPlay(id, t, gen);
  } catch (e) {
    if (gen !== generation) return;
    if (!browserTTS) {
      state = { activeId: id, status: "idle", progress: 0 };
      emit();
      toast.error("Read-aloud isn't available in this browser.");
      return;
    }
    // Silent, seamless fallback — no toast, no indicator.
    browserPlay(id, t, gen);
  }
}

export function pause() {
  if (state.status !== "playing") return;
  if (mode === "server" && audioEl) {
    try { audioEl.pause(); } catch (e) { /* ignore */ }
  } else if (mode === "browser" && synth) {
    try { synth.pause(); } catch (e) { /* ignore */ }
  }
  state = { ...state, status: "paused" };
  emit();
}

export function resume() {
  if (state.status !== "paused") return;
  if (mode === "server" && audioEl) {
    try { audioEl.play(); } catch (e) { /* ignore */ }
  } else if (mode === "browser" && synth) {
    try { synth.resume(); } catch (e) { /* ignore */ }
  }
  state = { ...state, status: "playing" };
  emit();
}

export function toggle(id, text) {
  if (state.activeId === id) {
    if (state.status === "playing") pause();
    else if (state.status === "paused") resume();
    else if (state.status === "loading") stop();
    else play(id, text);
  } else {
    play(id, text);
  }
}

// Silently warms the server cache for this text/voice so the first real Play
// press is instant. Gated by the admin "Pre-load audio" toggle on the caller side.
export async function prefetch(text, sessionId) {
  const t = (text || "").trim();
  if (!t) return;
  try {
    await fetch(`${API}/tts`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: t, voice: currentVoice, session_id: sessionId, prefetch: true }),
    });
  } catch (e) { /* best-effort warm-up, never surfaced to the user */ }
}

// ---- Amazon Polly server playback -------------------------------------
async function serverPlay(id, text, gen) {
  mode = "server";
  const el = ensureAudio();
  // Prime for iOS/Safari's autoplay policy: start playback synchronously within
  // the user gesture, then swap in the real audio once it's fetched.
  realPlaying = false;
  try { el.src = SILENT; await el.play(); } catch (e) { /* ignore */ }
  const res = await fetch(`${API}/tts`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, voice: currentVoice }),
  });
  if (gen !== generation) return;
  if (!res.ok) throw new Error(`tts ${res.status}`);
  const blob = await res.blob();
  if (gen !== generation) return;
  revokeUrl();
  currentUrl = URL.createObjectURL(blob);
  el.src = currentUrl;
  el.playbackRate = playbackRate;
  realPlaying = true;
  await el.play();
  if (gen !== generation) return;
  state = { activeId: id, status: "playing", progress: 0 };
  emit();
}

// ---- Free browser Web Speech playback --------------------------------------
// Speaks sentence-by-sentence and reports progress from real `boundary`/`start`
// events, so the read-along highlight tracks the actual voice accurately. The
// char offsets match ReadAlongPanel's ranges (sum of sentence length + 1).
let bSentences = [];
let bPrefix = [];
let bTotal = 1;
let bIdx = 0;
let bVoice = null;

function pickBrowserVoice() {
  if (!synth) return null;
  const voices = (synth.getVoices() || []).filter((v) => !isBlocked(v));
  if (!voices.length) return null;
  // Fallback voice: exact match on "Google US English" if this device has it.
  const forced = voices.find((v) => (v.name || "").trim().toLowerCase() === FALLBACK_VOICE_NAME.toLowerCase());
  if (forced) return forced;
  const en = voices.filter((v) => /^en(\b|[-_])/i.test(v.lang || ""));
  const pool = en.length ? en : voices;
  return pool.slice().sort((a, b) => scoreVoice(b) - scoreVoice(a))[0] || voices[0];
}

function browserStop() {
  bSentences = [];
  bPrefix = [];
  bTotal = 1;
  bIdx = 0;
  if (synth) {
    try { synth.cancel(); } catch (e) { /* ignore */ }
  }
}

function setBrowserProgress(chars) {
  state = { ...state, progress: Math.min(0.999, Math.max(0, chars / bTotal)) };
  emit();
}

function browserNext(gen) {
  if (gen !== generation || !synth) return;
  if (bIdx >= bSentences.length) { finish(); return; }
  const i = bIdx;
  const part = bSentences[i];
  const u = new SpeechSynthesisUtterance(part);
  bVoice = bVoice || pickBrowserVoice();
  if (bVoice) { u.voice = bVoice; u.lang = bVoice.lang; }
  u.rate = playbackRate;
  // Highlight this sentence the instant it begins…
  u.onstart = () => { if (gen === generation) setBrowserProgress(bPrefix[i]); };
  // …and glide within it as each word is spoken (where the browser supports it).
  u.onboundary = (e) => {
    if (gen !== generation) return;
    const ci = typeof e.charIndex === "number" ? e.charIndex : 0;
    setBrowserProgress(bPrefix[i] + ci);
  };
  u.onend = () => {
    if (gen !== generation) return;
    bIdx += 1;
    setBrowserProgress(bPrefix[i] + part.length + 1);
    browserNext(gen);
  };
  u.onerror = () => { if (gen === generation) stop(); };
  synth.speak(u);
}

function browserPlay(id, text, gen) {
  mode = "browser";
  bSentences = splitSentences(text);
  if (!bSentences.length) bSentences = [String(text || "")];
  bPrefix = [];
  let acc = 0;
  for (const s of bSentences) { bPrefix.push(acc); acc += s.length + 1; }
  bTotal = acc || 1;
  bIdx = 0;
  bVoice = pickBrowserVoice();
  state = { activeId: id, status: "playing", progress: 0 };
  emit();
  browserNext(gen);
}

export function useAudioReaderState() {
  const [snap, setSnap] = useState(state);
  useEffect(() => subscribe(setSnap), []);
  return snap;
}
